/**
 * Which of a negotiation's timeline entries the page's Activity popover shows (#5648).
 *
 * Each field's history control reads the entries at its own path. Some entries belong to no field
 * control on the page: the lifecycle events (submitted, approved, accepted, rejected, canceled,
 * hired), reviewer administration, template selections, `/negotiable`, the client representative and
 * the team's identity metadata. Before #5567 only the whole-proposal History drawer showed them. The
 * Activity popover shows those entries and no others, so it never repeats what a field shows.
 *
 * A page names what its field controls cover. The rest of the stream is activity.
 */
export type ProposalActivityCoverage = {
  /** The timeline paths that a field control on the page reads. */
  readonly coveredPaths: readonly string[];
};

/** The party whose policies a page draws. The draft page draws only the viewer's own party's. */
export type ProposalActivityPolicyParties = "both" | "client" | "vendor";

export type ProposalActivityCoverageOptions = {
  /**
   * The page has a history control for the whole vendor team. The archive does; the live pages give
   * each member and the leader a control of their own and have none for the team as a whole.
   */
  wholeTeamHistory?: boolean;
  /**
   * The team has a leader, so the leader row and its history control are drawn. When the leader is
   * cleared the row goes, and the leader's history goes with it unless the Activity popover shows it.
   */
  hasTeamLeader?: boolean;
  /** Defaults to "both". */
  policyParties?: ProposalActivityPolicyParties;
};

/** Paths every proposal page draws a history control for. */
const fieldControlPaths: readonly string[] = [
  "/name",
  "/description",
  "/responseDueBy",
  "/startDate",
  "/endDate",
  "/teamRestricted",
  "/feeSchedule",
  "/conflicts",
  "/conflictsCheckWaived",
  "/discount",
];

export function proposalActivityCoverage(options: Readonly<ProposalActivityCoverageOptions> = {}): ProposalActivityCoverage {
  const policyParties = options.policyParties ?? "both";
  const teamPaths = options.wholeTeamHistory
    ? ["/vendorTeam"]
    : ["/vendorTeam/members", ...(options.hasTeamLeader ? ["/vendorTeam/leader"] : [])];
  const policyPaths = [
    ...(policyParties === "vendor" ? [] : ["/clientPolicies"]),
    ...(policyParties === "client" ? [] : ["/vendorPolicies"]),
  ];
  return { coveredPaths: [...fieldControlPaths, ...teamPaths, ...policyPaths] };
}

/**
 * Whether a field control reads this path. A control's path covers itself and every path below it at
 * a `/` boundary, as the server's thread filter does (`ProposalNegotiationConversation.cs`):
 * `/vendorTeam` covers `/vendorTeam/leader`, but `/vendorTeam` does not cover `/vendorTeamTemplateIds`.
 */
export function proposalTimelinePathIsCovered(path: string, coverage: ProposalActivityCoverage): boolean {
  return coverage.coveredPaths.some((covered) => path === covered || path.startsWith(`${covered}/`));
}

/** An entry with no path is a lifecycle event, which no field control reads. */
export function isProposalActivityEntry(
  entry: Readonly<{ path?: string | null }>,
  coverage: ProposalActivityCoverage,
): boolean {
  return !entry.path || !proposalTimelinePathIsCovered(entry.path, coverage);
}
